import {
  type FactCheckClaim,
  type FactCheckReport,
  type FactCheckVerdict,
  type StoredFactCheck,
  extractFactCheckClaims,
  groupByChapter,
  hasLegalDutyCue,
  inferDefaultCode,
  statuteSectionUrl,
} from './article-factcheck';

/**
 * FIXING WHAT THE CLAIM CHECK FOUND — pure. Improve/revise never saw the claim
 * check (2026-10-03: ITC articles were revised, then claim-checked, and the wrong
 * sentences came through untouched because the writer had never been told which they
 * were). This turns a stored check into the instructions a revise needs, in code and
 * deterministically, so the instruction is the record and not someone's retelling.
 */

/** The revise request allows 4,000 characters; stay a little under. */
export const FACTCHECK_FIX_FEEDBACK_MAX_CHARS = 3_900;
/** Items given to the writer as must-fix context, so a large check cannot swamp the prompt. */
export const FACTCHECK_WRITER_MAX_ITEMS = 12;
const MIN_SURVIVOR_CHARS = 30;
const MAX_READ_PLAN_URLS = 6;

export type FactCheckFixPriority = 'must_fix' | 'should_fix';

export interface FactCheckFixItem {
  claimId: string;
  priority: FactCheckFixPriority;
  /** The sentence in the article, as the claim check extracted it. */
  sentence: string;
  verdict: FactCheckVerdict;
  citation: string | null;
  /** What the source says instead, when the check recorded it. */
  correction: string;
  /** The exact passage of the source the verdict rests on. */
  quote: string;
  sourceUrl: string | null;
  /** The opening of the section the check read, for context beyond the quote. */
  excerpt: string;
  reason: string;
}

const VERDICT_PHRASE: Partial<Record<FactCheckVerdict, string>> = {
  contradicted: 'the statute says otherwise',
  misapplied: 'the provision does not reach what this sentence applies it to',
  unsupported: 'the cited provision does not state this',
};

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function clip(text: string, max: number): string {
  const flat = collapse(text);
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, Math.max(0, max - 3)).replace(/[\uD800-\uDBFF]$/, '');
  return `${cut.replace(/\s+\S*$/, '') || cut}...`;
}

function withoutFragment(url: string): string {
  return url.split('#')[0] ?? url;
}

function readExcerpt(report: FactCheckReport, claim: FactCheckClaim): string {
  if (!claim.sourceUrl) return '';
  const fragment = claim.sourceUrl.split('#')[1] ?? null;
  const source = report.sources.find(
    (candidate) => withoutFragment(candidate.url) === withoutFragment(claim.sourceUrl!),
  );
  const reads = source?.reads ?? [];
  const read = (fragment ? reads.find((entry) => entry.section === fragment) : null) ?? reads[0];
  return read?.excerpt ?? '';
}

/**
 * A claim that asserts a legal rule, so that "the cited provision does not state this" is a finding about
 * the article. A date or a happening (`event`) is not one. A deadline or a figure is one only when its
 * sentence states a duty: a record written before events were set apart can hold a "deadline" that is
 * really a news date, and rewriting it would change a true fact.
 */
function assertsALegalRule(claim: Pick<FactCheckClaim, 'kind' | 'text'>): boolean {
  if (claim.kind === 'statute' || claim.kind === 'uncited_rule') return true;
  return (claim.kind === 'deadline' || claim.kind === 'figure') && hasLegalDutyCue(claim.text);
}

/**
 * Wrong law first (contradicted or misapplied: what fails the check), then claims
 * the cited provision does not state, when they assert a legal rule. Unverifiable is
 * never listed: nothing was found wrong. Chapter-only citations are informational and
 * never listed, and neither is a news or timeline statement.
 */
export function factCheckFixItems(report: FactCheckReport): FactCheckFixItem[] {
  const items: FactCheckFixItem[] = [];
  for (const claim of report.claims) {
    if (claim.informational) continue;
    if (claim.kind === 'event' || claim.verdict === 'not_applicable') continue;
    // A sentence that passes on what a news page or a county resolution says is judged against that page. The
    // instruction below is a rewrite against statute text, so it is not offered for one.
    if (claim.attribution) continue;
    const priority: FactCheckFixPriority | null =
      (claim.verdict === 'contradicted' || claim.verdict === 'misapplied') &&
      assertsALegalRule(claim)
        ? 'must_fix'
        : claim.verdict === 'unsupported' && claim.kind !== 'figure' && assertsALegalRule(claim)
          ? 'should_fix'
          : null;
    if (!priority) continue;
    items.push({
      claimId: claim.id,
      priority,
      sentence: collapse(claim.text),
      verdict: claim.verdict,
      citation: claim.citation,
      correction: collapse(claim.correction),
      quote: collapse(claim.quote),
      sourceUrl: claim.sourceUrl,
      excerpt: collapse(readExcerpt(report, claim)),
      reason: collapse(claim.reason),
    });
  }
  return items.sort(
    (a, b) => Number(a.priority === 'should_fix') - Number(b.priority === 'should_fix'),
  );
}

/** True when a current check holds something to fix. A check of an earlier revision is history, not an instruction. */
export function hasFactCheckFixes(stored: StoredFactCheck | null | undefined): boolean {
  return Boolean(stored?.isCurrent && factCheckFixItems(stored.report).length > 0);
}

/** The must-fix subset a revise writer is handed, each field bounded. Nothing is shown to the writer that the check did not record. */
export function factCheckMustFixForWriter(
  stored: StoredFactCheck | null | undefined,
): FactCheckFixItem[] {
  if (!stored?.isCurrent) return [];
  return factCheckFixItems(stored.report)
    .filter((item) => item.priority === 'must_fix')
    .slice(0, FACTCHECK_WRITER_MAX_ITEMS)
    .map((item) => ({
      ...item,
      sentence: clip(item.sentence, 500),
      correction: clip(item.correction, 500),
      quote: clip(item.quote, 400),
      excerpt: clip(item.excerpt, 600),
      reason: clip(item.reason, 300),
    }));
}

const HEADER =
  'FIX THESE CLAIMS. The claim check read the statute text for each sentence below. Rewrite each so it states only what the quoted statute text says, keeping the rest of the article. If a sentence cannot be stated from the quoted text, remove it. Add no legal claim the quoted text does not support.';

/** Detail levels, richest first. A level applies to every item so the list degrades evenly. */
const LEVELS = [
  { sentence: 320, correction: 320, quote: 280, excerpt: 320, url: true },
  { sentence: 280, correction: 280, quote: 220, excerpt: 0, url: true },
  { sentence: 220, correction: 220, quote: 150, excerpt: 0, url: true },
  { sentence: 160, correction: 160, quote: 0, excerpt: 0, url: true },
  { sentence: 110, correction: 110, quote: 0, excerpt: 0, url: false },
] as const;

function renderItem(item: FactCheckFixItem, index: number, level: (typeof LEVELS)[number]) {
  const parts = [
    `${index + 1}) "${clip(item.sentence, level.sentence)}"`,
    `${item.citation ? `${item.citation}: ` : ''}${VERDICT_PHRASE[item.verdict] ?? item.verdict}${
      item.priority === 'should_fix' ? ' (soften or remove)' : ''
    }.`,
  ];
  if (item.correction) parts.push(`Correct reading: ${clip(item.correction, level.correction)}`);
  if (level.quote && item.quote) parts.push(`Statute text: "${clip(item.quote, level.quote)}"`);
  if (level.excerpt && item.excerpt && item.excerpt !== item.quote)
    parts.push(`Section as read: ${clip(item.excerpt, level.excerpt)}`);
  if (level.url && item.sourceUrl) parts.push(`Source: ${item.sourceUrl}`);
  return parts.join(' ');
}

/**
 * The revise feedback, built only from the stored check. Deterministic: the same
 * record gives the same text. Within `maxChars` the detail drops evenly (section
 * text, then quote length, then sentence length), and only when even the leanest
 * form does not fit are the last, least severe items left out and counted.
 */
export function buildFactCheckFixFeedback(
  stored: StoredFactCheck | null | undefined,
  maxChars: number = FACTCHECK_FIX_FEEDBACK_MAX_CHARS,
): string {
  if (!stored?.isCurrent) return '';
  const items = factCheckFixItems(stored.report);
  if (!items.length) return '';
  const compose = (shown: readonly FactCheckFixItem[], level: (typeof LEVELS)[number]) => {
    const omitted = items.length - shown.length;
    const footer = omitted
      ? `\n${omitted} further claim${omitted === 1 ? '' : 's'} flagged by the claim check ${
          omitted === 1 ? 'is' : 'are'
        } not repeated here.`
      : '';
    return `${HEADER}\n${shown.map((item, index) => renderItem(item, index, level)).join('\n')}${footer}`;
  };
  const leanest = LEVELS[LEVELS.length - 1]!;
  for (const level of LEVELS) {
    const text = compose(items, level);
    if (text.length <= maxChars) return text;
  }
  for (let count = items.length - 1; count >= 1; count -= 1) {
    const text = compose(items.slice(0, count), leanest);
    if (text.length <= maxChars) return text;
  }
  return clip(compose(items.slice(0, 1), leanest), maxChars);
}

function squashAlnum(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * Must-fix sentences still present in a revision, word for word (ignoring
 * markdown, punctuation and case). A revise that was told a sentence is wrong and
 * kept it has not done what it was asked, and no model has to notice: this is the
 * check.
 */
export function survivingMustFix(
  items: readonly FactCheckFixItem[],
  markdown: string,
): FactCheckFixItem[] {
  if (!items.length || !markdown) return [];
  const haystack = squashAlnum(markdown);
  return items.filter((item) => {
    const needle = squashAlnum(item.sentence);
    return needle.length >= MIN_SURVIVOR_CHARS && haystack.includes(needle);
  });
}

export interface StatuteReadTarget {
  /** Anchored at the lowest section cited from this chapter, so the retained text starts there. */
  url: string;
  sections: string[];
}

/**
 * The statute pages an article's cited sections live on, one anchored URL per
 * chapter (the retained 24,000 characters then start at the lowest cited section
 * instead of the top of the chapter). A revise that cites a section research never
 * read fails `claims_found_in_sources` and is set aside as an alternate; handing the
 * researcher these URLs is what lets the rewrite cite what it has read.
 */
export function statuteReadPlan(markdown: string): StatuteReadTarget[] {
  if (!markdown.trim()) return [];
  const { claims } = extractFactCheckClaims(markdown, { defaultCode: inferDefaultCode(markdown) });
  const refs = claims.flatMap((claim) => claim.refs).filter((ref) => ref.section && !ref.via);
  return groupByChapter(refs)
    .filter((group) => group.sections.length > 0)
    .slice(0, MAX_READ_PLAN_URLS)
    .map((group) => ({
      url: statuteSectionUrl(group.code, group.chapter, group.sections[0]!.section!),
      sections: [...new Set(group.sections.map((ref) => ref.section!))],
    }));
}
